import { z } from 'zod'
import type { DocumentType } from '../jobcard-status.js'
import { carSnapshotSchema } from './car.js'
import { lineKindSchema } from './catalog.js'
import { basisPointsSchema, idSchema, isoDateTimeSchema, minorSchema } from './common.js'

export const documentTypeSchema = z.enum(['quotation', 'invoice'])
export const DOCUMENT_TYPE_TITLES: Record<DocumentType, string> = {
  quotation: 'QUOTATION',
  invoice: 'TAX INVOICE',
}

/**
 * A document's frozen copy of everything it prints.
 *
 * This is the whole reason documents are immutable: regenerating an invoice
 * from 2024 must not pick up today's business address, today's VAT rate or a
 * price someone corrected last week. The renderer reads ONLY from here.
 */
export const documentSnapshotSchema = z.object({
  /** Which kind of document this is. The renderer titles the page from it. */
  type: documentTypeSchema,
  /**
   * This document's own number, e.g. INV-2026-0007 — NOT the jobcard's.
   * Frozen here because the renderer only ever sees the snapshot, and an
   * invoice that cannot print its own number is not a valid document.
   */
  documentNumber: z.string().default(''),
  business: z.object({
    name: z.string(),
    address: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    taxNumber: z.string().nullable(),
    website: z.string().nullable(),
    logoPath: z.string().nullable(),
  }),
  client: z.object({
    type: z.enum(['individual', 'company']).default('individual'),
    /** `clientDisplayName()` at the time of issue. */
    name: z.string(),
    phone: z.string(),
    /** Company only. A UAE tax invoice must show the recipient's TRN. */
    trn: z.string().nullable().default(null),
    tradeLicence: z.string().nullable().default(null),
    /** Company only. The recipient's address, also required on a tax invoice. */
    billingAddress: z.string().nullable().default(null),
  }),
  car: carSnapshotSchema,
  jobcard: z.object({
    number: z.string(),
    complaint: z.string().nullable(),
    odometerKm: z.number().int().nullable(),
    orderRef: z.string().nullable().default(null),
    appointmentAt: z.string().nullable().default(null),
  }),
  lines: z.array(
    z.object({
      kind: lineKindSchema,
      name: z.string(),
      partNumber: z.string().nullable(),
      quantityMilli: z.number().int(),
      rateMinor: minorSchema,
      lineTotalMinor: minorSchema,
      vatMinor: minorSchema.default(0),
      afterVatMinor: minorSchema.default(0),
    }),
  ),
  totals: z.object({
    subtotalMinor: minorSchema,
    /** Shown as its own line on the document when non-zero. */
    discountMinor: minorSchema.default(0),
    /** subtotal - discount; the amount VAT was charged on. */
    taxableMinor: minorSchema.default(0),
    vatMinor: minorSchema,
    totalMinor: minorSchema,
    /** Amount the customer has already paid, in minor units. */
    amountPaidMinor: minorSchema.default(0),
    /**
     * True when the money was recorded as a dated deposit rather than an
     * unexplained "already paid". Only changes what the row is CALLED — the
     * arithmetic is the same either way.
     */
    paidIsDeposit: z.boolean().default(false),
    /** Total due (total - paid). Never negative. */
    amountDueMinor: minorSchema.default(0),
  }),
  vat: z.object({
    enabled: z.boolean(),
    rateBp: basisPointsSchema,
    label: z.string(),
  }),
  currency: z.object({
    code: z.string(),
    symbol: z.string(),
    decimals: z.number().int(),
  }),
  terms: z.object({
    footer: z.string(),
    bankDetails: z.string(),
    /** ISO date; quotations only. */
    validUntil: z.string().nullable(),
    /** ISO date; invoices only. Equal to issue date when due on receipt. */
    dueDate: z.string().nullable(),
    /**
     * Invoices only. Terms & conditions chosen for THIS invoice, printed alone
     * on the back of the sheet (page 2). Empty means no back page.
     */
    backPage: z.string().default(''),
  }),
  bank: z.object({
    name: z.string().default(''),
    accountTitle: z.string().default(''),
    accountNumber: z.string().default(''),
    iban: z.string().default(''),
    branchCode: z.string().default(''),
  }).default({}),
  /** Timestamp when the document was issued. ISO-8601. */
  printedAt: isoDateTimeSchema.default(''),
  dateFormat: z.string(),
})
export type DocumentSnapshot = z.infer<typeof documentSnapshotSchema>

/**
 * NOTE: the snapshot deliberately contains NO cost figures. Our cost must never
 * reach a customer document, and the cleanest guarantee is that the renderer
 * has no access to it in the first place.
 */

export const BACK_TERMS_MAX_LENGTH = 5000

export const createDocumentSchema = z.object({
  type: documentTypeSchema,
  /** Invoices only: terms & conditions to print on the back page. Ignored for quotations. */
  backTerms: z.string().trim().max(BACK_TERMS_MAX_LENGTH).optional(),
})
export type CreateDocumentRequest = z.infer<typeof createDocumentSchema>

export const documentSchema = z.object({
  id: idSchema,
  jobcardId: idSchema,
  type: documentTypeSchema,
  number: z.string(),
  issuedAt: isoDateTimeSchema,
  snapshot: documentSnapshotSchema,
  subtotalMinor: minorSchema,
  discountMinor: minorSchema.default(0),
  vatMinor: minorSchema,
  totalMinor: minorSchema,
  /** Relative to DATA_DIR/documents. */
  pdfPath: z.string(),
})
export type GarageDocument = z.infer<typeof documentSchema>

/** A document as listed on a jobcard: no snapshot, so the payload stays small. */
export interface DocumentSummary {
  id: string
  jobcardId: string
  type: DocumentType
  number: string
  issuedAt: string
  subtotalMinor: number
  discountMinor: number
  vatMinor: number
  totalMinor: number
}
